import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import "./App.css";
import heroPoster from "./assests/image.png";
import mriecqr from "./assests/mriecqr.png";

/* ================= ANIMATION VARIANTS ================= */

const container = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

const fadeLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
};

const fadeRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } }
};

const slideInUp = {
  hidden: { opacity: 0, y: 100 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
};

const rotateIn = {
  hidden: { opacity: 0, rotate: -10 },
  visible: { opacity: 1, rotate: 0, transition: { duration: 0.7 } }
};

/* ================= APP ================= */

export default function App() {
  return (
    <Router>
      <div className="page">
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about-us" element={<ComingSoon title="About Us" />} />
          <Route path="/success-stories" element={<ComingSoon title="Success Stories" />} />
          <Route path="/contact-us" element={<ComingSoon title="Contact Us" />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

/* ================= NAVBAR ================= */

function Navbar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about-us" },
    { label: "Success Stories", path: "/success-stories" },
    { label: "Contact Us", path: "/contact-us" }
  ];

  const handleMenuClose = () => setIsMenuOpen(false);

  return (
    <motion.nav
      className="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={handleMenuClose}>
          MRIEC
        </Link>
        
        {/* Hamburger Menu Button */}
        <motion.button
          className={`hamburger ${isMenuOpen ? "active" : ""}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </motion.button>

        {/* Navigation Menu */}
        <motion.div 
          className={`nav-menu ${isMenuOpen ? "active" : ""}`}
          initial={false}
          animate={isMenuOpen ? "open" : "closed"}
          variants={{
            open: { opacity: 1, x: 0 },
            closed: { opacity: 0, x: "100%" }
          }}
          transition={{ duration: 0.3 }}
        >
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? "active" : ""}`}
              onClick={handleMenuClose}
            >
              {item.label}
            </Link>
          ))}
        </motion.div>
      </div>
    </motion.nav>
  );
}

/* ================= HOME PAGE ================= */

function Home() {
  return (
    <>
      <Hero />
      <VisionMission />
      <WhyStudyAbroad />
      <Services />
      <Objectives />
      <Process />
      <Stats />
      <CTA />
    </>
  );
}

/* ================= COMING SOON PAGE ================= */

function ComingSoon({ title }) {
  return (
    <motion.section
      className="coming-soon"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="coming-soon-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h1>{title}</h1>
        <p>Coming Soon</p>
        <Link to="/" className="btn-primary">
          Back to Home
        </Link>
      </motion.div>
    </motion.section>
  );
}

/* ================= HERO ================= */

function Hero() {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <section className="hero">
      {/* LEFT CONTENT */}
      <motion.div
        className="hero-left"
        variants={fadeLeft}
        initial="hidden"
        animate="visible"
      >
        <h1 className="hero-title">
          YOUR GATEWAY TO <br />
          <span className="hero-highlight">
            INTERNATIONAL <br /> EDUCATION
          </span>
        </h1>

        <p className="hero-subtitle">
          MR International Education Centre (MRIEC) – Global Education Connect
        </p>

        <div className="hero-buttons">
          <motion.button
            className="btn-primary"
            whileHover={{ scale: 1.05, boxShadow: "0 20px 50px rgba(0, 102, 204, 0.5)" }}
            whileTap={{ scale: 0.98 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span className="btn-text">Free Consultation</span>
            <motion.span 
              className="btn-icon"
              animate={isHovered ? { x: 5 } : { x: 0 }}
            >
              →
            </motion.span>
          </motion.button>

          <motion.button
            className="btn-secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Explore Programs
          </motion.button>
        </div>
      </motion.div>

      {/* RIGHT IMAGE */}
      <motion.div
        className="hero-right"
        variants={fadeRight}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="hero-image-wrapper" whileHover={{ scale: 1.03 }}>
          <img
            src={heroPoster}
            alt="MR International Education Centre"
            className="hero-image"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}


/* ================= SECTION WRAPPER ================= */

function Section({ title, children, alt }) {
  return (
    <motion.section
      className={alt ? "section alt" : "section"}
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <motion.h2 className="section-title" variants={fadeUp}>
        {title}
      </motion.h2>
      {children}
    </motion.section>
  );
}

/* ================= CONTENT ================= */

function VisionMission() {
  return (
    <Section title="Our Vision & Mission">
      <div className="card-grid">
        {[
          "Create lasting global academic partnerships",
          "Transform students into confident global professionals"
        ].map((text, i) => (
          <motion.div key={i} className="card interactive-card" variants={scaleIn}>
            <motion.div className="card-content">
              <div className="card-number">{i + 1}</div>
              <p>{text}</p>
            </motion.div>
            <motion.div 
              className="card-bg"
              whileHover={{ rotate: 5, scale: 1.1 }}
            />
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function WhyStudyAbroad() {
  const [selectedId, setSelectedId] = useState(null);

  const benefits = [
    "International Exposure",
    "Enhanced Career Prospects",
    "World-Class Education",
    "Multicultural Experience",
    "Personal Development",
    "Research Excellence"
  ];

  return (
    <Section title="Why Study Abroad?" alt>
      <div className="points-grid">
        {benefits.map((text, i) => (
          <motion.div 
            key={i} 
            className={`point-box interactive-point ${selectedId === i ? "selected" : ""}`}
            variants={rotateIn}
            onClick={() => setSelectedId(selectedId === i ? null : i)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.span 
              className="point-number"
              animate={selectedId === i ? { scale: 1.2, color: "#4db8ff" } : { scale: 1, color: "#0066cc" }}
            >
              {String(i + 1).padStart(2, "0")}
            </motion.span>
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={selectedId === i ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="point-description"
            >
              Explore world-class opportunities and expand your horizons
            </motion.div>
            {text}
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Services() {
  const [hoveredId, setHoveredId] = useState(null);

  const services = [
    "Test Preparation",
    "University Selection",
    "Application Support",
    "Document Writing",
    "Visa Assistance",
    "Student Support"
  ];

  return (
    <Section title="Our Services">
      <div className="services-grid">
        {services.map((title, i) => (
          <motion.div 
            key={i} 
            className="service-card" 
            variants={slideInUp}
            onMouseEnter={() => setHoveredId(i)}
            onMouseLeave={() => setHoveredId(null)}
            whileHover={{ y: -15 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              className="service-content"
              animate={hoveredId === i ? { scale: 1.05 } : { scale: 1 }}
            >
              <div className="service-number">{i + 1}</div>
              <h3>{title}</h3>
              <motion.div 
                className="service-dot"
                animate={hoveredId === i ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
              />
            </motion.div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Objectives() {
  return (
    <Section title="Our Objectives" alt>
      <div className="points-grid">
        {[
          "Global University Network",
          "Dual Degree Programs",
          "Research Initiatives",
          "Internship Opportunities",
          "Exchange Programs"
        ].map((text, i) => (
          <motion.div key={i} className="point-box" variants={fadeUp}>
            {/* <span className="point-number">{String(i + 1).padStart(2, "0")}</span> */}
            {text}
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Process() {
  const [expandedId, setExpandedId] = useState(null);

  const steps = [
    { title: "Career Counseling", desc: "Personalized guidance based on your goals" },
    { title: "University Shortlisting", desc: "Curated list of best-fit institutions" },
    { title: "Application Submission", desc: "Complete application package preparation" },
    { title: "Visa Preparation", desc: "Expert visa documentation support" },
    { title: "Pre-Departure Training", desc: "Get ready for your international journey" },
    { title: "Graduate Support", desc: "Ongoing assistance after graduation" }
  ];

  return (
    <Section title="Our Process">
      <div className="card-grid">
        {steps.map((step, i) => (
          <motion.div 
            key={i} 
            className="card process-card"
            variants={scaleIn}
            onClick={() => setExpandedId(expandedId === i ? null : i)}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <motion.div className="process-header">
              <div className="step-badge">{i + 1}</div>
              <strong className="step-label">Step {i + 1}</strong>
            </motion.div>
            <p className="step-title">{step.title}</p>
            <motion.div
              initial={{ opacity: 0, maxHeight: 0 }}
              animate={expandedId === i ? { opacity: 1, maxHeight: 100 } : { opacity: 0, maxHeight: 0 }}
              transition={{ duration: 0.3 }}
              className="step-description"
            >
              {step.desc}
            </motion.div>
            <motion.span 
              className="expand-icon"
              animate={expandedId === i ? { rotate: 180 } : { rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              ▼
            </motion.span>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Stats() {
  const [isVisible, setIsVisible] = useState(false);
  const stats = [
    ["1000+", "Students Placed"],
    ["50+", "Partner Universities"],
    ["15+", "Countries Covered"],
    ["98%", "Success Rate"]
  ];

  const StatCounter = ({ value, label }) => {
    const numValue = parseInt(value);
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;
      let timer;
      const target = numValue;
      const increment = Math.max(1, Math.floor(target / 30));
      
      timer = setInterval(() => {
        setCount(prev => {
          const next = prev + increment;
          return next >= target ? target : next;
        });
      }, 50);
      
      return () => clearInterval(timer);
    }, [ numValue]);

    return (
      <motion.div className="stat-box" variants={slideInUp} onViewportEnter={() => setIsVisible(true)}>
        <h3 className="stat-value">
          {count}{value.replace(/[0-9]/g, '')}
        </h3>
        <p>{label}</p>
      </motion.div>
    );
  };

  return (
    <motion.section
      className="stats"
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {stats.map(([value, label], i) => (
        <StatCounter key={i} value={value} label={label} />
      ))}
    </motion.section>
  );
}

function CTA() {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.section
      className="cta"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Begin Your International Education Journey
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        Connect with our expert counselors today for personalized guidance
      </motion.p>

      {/* QR + Button Wrapper */}
      <div className="cta-actions">
        {/* QR CODE */}
        <motion.div
          className="cta-qr"
          initial={{ opacity: 0, scale: 0.8, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          whileHover={{ scale: 1.1 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.img
            src={mriecqr}
            alt="Scan to Contact MRIEC"
            className="qr-image"
            animate={isHovered ? { rotate: 5 } : { rotate: 0 }}
          />
          <motion.span 
            className="qr-text"
            animate={isHovered ? { y: -5 } : { y: 0 }}
          >
            Scan for Quick Registration
          </motion.span>
        </motion.div>

        {/* CTA BUTTON */}
        <motion.button
          className="cta-btn"
          initial={{ opacity: 0, scale: 0.8, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          whileHover={{ scale: 1.08, boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)" }}
          whileTap={{ scale: 0.95 }}
        >
          <span>Schedule Consultation</span>
          <motion.span
            className="btn-arrow"
            animate={{ x: 0 }}
            transition={{ duration: 0.3 }}
          >
            →
          </motion.span>
        </motion.button>
      </div>
    </motion.section>
  );
}


function Footer() {
  return (
    <footer className="footer">
      © 2026 MR International Education Centre
    </footer>
  );
}
